// ─── Preliminary Quote → QuotationDocumentData mapper ─────────────────────────
// Display-only reshaping of an immutable Backend snapshot. No pricing, no FX,
// no total recalculation. Monetary values are the Backend strings, coerced to
// numbers for the canonical (single-currency) view model.

import type { CustomerQuoteDetailData } from '@/lib/quotationTypes';
import type { QuotationDocumentData } from '@/quotation/document/types';
import {
  normalizeDocumentLocale,
  toNumber,
  mapSeller,
  mapBuyer,
  mapItem,
  mapExchangeRate,
} from './shared';

export function mapPreliminaryQuoteToDocument(
  quote: CustomerQuoteDetailData,
): QuotationDocumentData {
  // Core commercial data — fail loudly rather than render a broken document (§26).
  if (!quote.quoteNumber) {
    throw new Error('Invalid preliminary quote: missing quotation number.');
  }
  if (!Array.isArray(quote.items)) {
    throw new Error('Invalid preliminary quote: missing items.');
  }
  const total = toNumber(quote.totals?.quoteTotal);
  if (total == null) {
    throw new Error('Invalid preliminary quote: missing total.');
  }

  const currency = quote.currency?.code ?? 'USD';

  return {
    documentType: 'preliminary_quote',
    documentLocale: normalizeDocumentLocale(quote.documentLocale),
    documentTemplateVersion: quote.pdfTemplateVersion ?? undefined,

    quotationNumber: quote.quoteNumber,
    issuedAt: quote.createdAt ?? '',

    seller: mapSeller(quote.seller),
    buyer: mapBuyer(quote.buyer),

    items: quote.items.map(mapItem),

    currency,

    // PQ totals are exposed in USD only (productSubtotalUsd / finishingSubtotalUsd /
    // baseTotalUsd) plus the final quote-currency total (quoteTotal). When the quote
    // currency is USD these all align; the breakdown is intentionally the Backend USD
    // snapshot (no client-side conversion).
    productAmount: toNumber(quote.totals?.productSubtotalUsd),
    finishingAmount: toNumber(quote.totals?.finishingSubtotalUsd),
    subtotal: toNumber(quote.totals?.baseTotalUsd),
    total,

    exchangeRate: mapExchangeRate(quote.fx?.appliedFxRate),

    specialRequirements: quote.specialRequirements ?? undefined,
  };
}